import classNames from 'classnames'
import {
  type CSSProperties,
  FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { type CardTone } from 'partner360/components/card'

import { TileShell } from './components'
import { clampCount, getResponsiveColumns } from './helpers/gridSizing'
import { type TileSizing } from './helpers/tileSizing'

import styles from './TileBoard.module.scss'

type StretchTransform = {
  scaleX: number
  scaleY: number
  x: number
  y: number
}

// FLIP "invert": scale/translate the full-size overlay so it visually starts exactly
// where `source` was, relative to `container` (the overlay's own coordinate space).
const getStretchTransform = (source: HTMLElement, container: HTMLElement): StretchTransform => {
  const s = source.getBoundingClientRect()
  const c = container.getBoundingClientRect()
  return {
    scaleX: s.width / c.width,
    scaleY: s.height / c.height,
    x: s.left + s.width / 2 - (c.left + c.width / 2),
    y: s.top + s.height / 2 - (c.top + c.height / 2),
  }
}

type ActiveTile = { id: string; phase: 'open' | 'closing' }

export type TileConfig = TileSizing & {
  id: string
  title: string
  subtitle: string
  hideExpandButton?: boolean
  tone?: CardTone
  /** Greyed and faded: the tile holds nothing to act on right now. */
  muted?: boolean
  /** Ringed for a moment: the tile's data has just been refreshed. */
  highlighted?: boolean
  /** Custom header content in place of the default title/subtitle markup. Receives
   *  the same expanded/onToggle the tile's own toggle button uses, so custom
   *  content can react to the tile's expand state. */
  renderTitle?: (context: {
    title: string
    subtitle?: string
    expanded: boolean
    onToggle: () => void
  }) => ReactNode
  /** How many grid columns/rows this tile occupies. Both default to 1. Use
   *  MAX_GRID_COUNT (from ./gridSizing) as colSpan for a tile that should always span
   *  the board's full width, regardless of its configured `columns`. */
  colSpan?: number
  rowSpan?: number
  /** `data-tour-id` for this tile as it sits on the board. */
  tourId?: string
  /** `data-tour-id` for this tile's maximized overlay. It must differ from `tourId`: while a
   *  tile is expanded its grid copy stays mounted at `opacity: 0`, and the tour resolves a
   *  target by the first `[data-tour-id]` match in document order — which is that grid copy. */
  expandedTourId?: string
  renderCollapsed: (controls: { expand: () => void }) => ReactNode
  renderExpanded: (controls: { collapse: () => void }) => ReactNode
}

/** An existing card outside the grid can use the board's regular expanded surface. */
export type ExternalTileConfig = Omit<TileConfig, 'renderCollapsed'> & {
  getExpandSource?: () => HTMLElement | null
}

interface ITileBoardProps {
  tiles: TileConfig[]
  externalTiles?: ExternalTileConfig[]
  /** Column count target on wide screens (≥ 1180px); below that the board runs a single
   *  tile per row, whatever this asks for. */
  columns: number
  /** Visible viewport height, in rows. Omit to fill the ambient container's height
   *  instead (extra tiles always scroll into view either way). */
  rows?: number
  /** On wide screens, fills the ambient height by stretching only the final configured row. */
  stretchLastRow?: boolean
  /** Fits the board into the height it is given: the rows split that height between them, the
   *  board itself never scrolls, and each tile scrolls its own content. */
  fitHeight?: boolean
  /** Explicit sizes for the leading rows, as CSS track sizes (`'auto'`, `'min-content'`,
   *  `'200px'`, `'1fr'`, `'minmax(160px, 25dvh)'`). Rows past the list keep the board's own
   *  sizing. `['auto']` with `fitHeight` gives the first row exactly its content height and
   *  leaves the rest to the rows below. Overrides `stretchLastRow`'s row template. */
  rowSizes?: string[]
  /** Which animation plays when a tile expands/collapses. Defaults to 'zoom' (fade +
   *  scale from the overlay's own center). 'stretch' grows the overlay out of the
   *  clicked tile's actual on-screen rect, Windows-maximize style, and mirrors it in
   *  reverse on collapse. */
  expandAnimation?: 'zoom' | 'stretch'
  /** Drives expansion from outside (a widget tour, for instance). Omit it and the board owns
   *  the state itself, which is what every caller that does not need to steer it should do. */
  expandedTileId?: string | null
  /** Fires whenever expansion changes, including when the user drives it from a tile. */
  onExpandedTileChange?: (tileId: string | null) => void
}

export const TileBoard: FC<ITileBoardProps> = ({
  tiles,
  externalTiles,
  columns,
  rows,
  stretchLastRow = false,
  fitHeight = false,
  rowSizes,
  expandAnimation = 'zoom',
  expandedTileId,
  onExpandedTileChange,
}) => {
  const safeColumns = clampCount(columns)
  const safeRows = rows === undefined ? undefined : clampCount(rows)
  const responsiveColumns = getResponsiveColumns(safeColumns)

  const dashboardRef = useRef<HTMLDivElement>(null)
  const tileRefs = useRef(new Map<string, HTMLElement>())

  const [activeTile, setActiveTile] = useState<ActiveTile | null>(null)
  const [stretch, setStretch] = useState<StretchTransform | null>(null)
  const expandedTile =
    tiles.find((t) => t.id === activeTile?.id) ??
    externalTiles?.find((t) => t.id === activeTile?.id) ??
    null

  const requestExpand = useCallback(
    (id: string) => {
      if (expandAnimation === 'stretch') {
        const source =
          tileRefs.current.get(id) ??
          externalTiles?.find((tile) => tile.id === id)?.getExpandSource?.()
        if (source && dashboardRef.current) {
          setStretch(getStretchTransform(source, dashboardRef.current))
        }
      }
      setActiveTile({ id, phase: 'open' })
      onExpandedTileChange?.(id)
    },
    [expandAnimation, onExpandedTileChange, externalTiles]
  )

  const requestClose = useCallback(() => {
    if (!activeTile) return
    onExpandedTileChange?.(null)
    if (expandAnimation === 'stretch') {
      const source =
        tileRefs.current.get(activeTile.id) ??
        externalTiles?.find((tile) => tile.id === activeTile.id)?.getExpandSource?.()
      if (source && dashboardRef.current) {
        setStretch(getStretchTransform(source, dashboardRef.current))
        setActiveTile({ id: activeTile.id, phase: 'closing' })
        return
      }
    }
    setActiveTile(null)
  }, [activeTile, expandAnimation, onExpandedTileChange, externalTiles])

  // Controlled mode mirrors the prop into the board's own state, which stays the single source
  // of truth for rendering and the expand animation. A tile mid-'closing' reads as collapsed
  // here, so an animation already in flight is never interrupted.
  useEffect(() => {
    if (expandedTileId === undefined) return

    const current = activeTile?.phase === 'open' ? activeTile.id : null

    if (expandedTileId === current) return

    if (expandedTileId) {
      requestExpand(expandedTileId)
    } else {
      requestClose()
    }
  }, [activeTile, expandedTileId, requestClose, requestExpand])

  useEffect(() => {
    if (!activeTile) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activeTile, requestClose])

  return (
    <div className={styles.dashboard} ref={dashboardRef}>
      {/* Scrollable grid — hidden behind the maximized tile. It comes back as the tile starts
          shrinking, so the tile lands among the others instead of on an empty board that only
          fills in afterwards. */}
      <div
        className={classNames(
          styles.scroller,
          { [styles['scroller--hidden']]: activeTile?.phase === 'open' },
          safeRows !== undefined && styles[`scroller--rows-${safeRows}`],
          stretchLastRow && styles['scroller--stretch-last-row'],
          fitHeight && styles['scroller--fit']
        )}
        aria-hidden={activeTile ? true : undefined}
        inert={activeTile !== null}
      >
        <div
          className={classNames(
            styles.grid,
            fitHeight && styles['grid--fit'],
            safeRows === undefined && !fitHeight && styles['grid--fill'],
            stretchLastRow && safeRows !== undefined && styles['grid--stretch-last-row'],
            stretchLastRow &&
              safeRows !== undefined &&
              styles[`grid--stretch-last-row-rows-${safeRows}`],
            styles[`cols-sm-${responsiveColumns.sm}`],
            styles[`cols-lg-${responsiveColumns.lg}`]
          )}
          style={rowSizes?.length ? { gridTemplateRows: rowSizes.join(' ') } : undefined}
        >
          {tiles.map((tile) => (
            <TileShell
              key={tile.id}
              hideExpandButton={tile.hideExpandButton}
              tone={tile.tone}
              muted={tile.muted}
              highlighted={tile.highlighted}
              title={tile.title}
              subtitle={tile.subtitle}
              renderTitle={tile.renderTitle}
              expanded={false}
              onToggle={() => requestExpand(tile.id)}
              onShellRef={(el) => {
                if (el) tileRefs.current.set(tile.id, el)
                else tileRefs.current.delete(tile.id)
              }}
              colSpan={tile.colSpan}
              rowSpan={tile.rowSpan}
              sizing={tile}
              gridColumns={responsiveColumns}
              tourId={tile.tourId}
            >
              {tile.renderCollapsed({ expand: () => requestExpand(tile.id) })}
            </TileShell>
          ))}
        </div>
      </div>

      {/* Maximized tile — covers the whole dashboard area */}
      {expandedTile ? (
        <div
          className={classNames(styles.overlay, {
            [styles['overlay--stretch']]: expandAnimation === 'stretch',
            [styles['overlay--closing']]: activeTile?.phase === 'closing',
          })}
          style={
            stretch
              ? ({
                  '--stretch-x': `${stretch.x}px`,
                  '--stretch-y': `${stretch.y}px`,
                  '--stretch-scale-x': stretch.scaleX,
                  '--stretch-scale-y': stretch.scaleY,
                } as CSSProperties)
              : undefined
          }
          onAnimationEnd={() => {
            if (activeTile?.phase === 'closing') setActiveTile(null)
          }}
        >
          <TileShell
            hideExpandButton={expandedTile.hideExpandButton}
            tone={expandedTile.tone}
            muted={expandedTile.muted}
            highlighted={expandedTile.highlighted}
            title={expandedTile.title}
            subtitle={expandedTile.subtitle}
            renderTitle={expandedTile.renderTitle}
            expanded
            onToggle={requestClose}
            tourId={expandedTile.expandedTourId}
          >
            {expandedTile.renderExpanded({ collapse: requestClose })}
          </TileShell>
        </div>
      ) : null}
    </div>
  )
}

TileBoard.displayName = 'TileBoard'
